import React, { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import {
  IServiceTicket,
  ServiceTicketType,
} from "../../models/serviceManagement.model";
import ServiceTicketCardModal from "./ServiceKanbanCardModal";

interface ServiceKanbanCardProps {
  ticket: IServiceTicket;
  status: string;
}

const ServiceKanbanCard: React.FC<ServiceKanbanCardProps> = ({
  ticket,
  status,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: ticket._id!,
    data: { ticket, status },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const getTicketTypeColor = (type: ServiceTicketType) => {
    switch (type) {
      case ServiceTicketType.monitoring:
        return "text-green-500";
      default:
        return "text-gray-300";
    }
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className="bg-white border border-gray-200 rounded-xl shadow-md 
                   hover:shadow-lg transition-all duration-200 p-4 select-none"
      >
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-gray-900 text-sm relative group max-w-[220px]">
            <span
              className={`text-sm mr-1 ${getTicketTypeColor(
                ticket.ticketType as ServiceTicketType
              )}`}
            >
              ●
            </span>

            <span className="inline-block align-middle">
              {ticket.customerName.length > 20
                ? `${ticket.customerName.slice(0, 20)}…`
                : ticket.customerName}
            </span>

            {/* Hover tooltip */}
            {ticket.customerName.length > 20 && (
              <span
                className="absolute left-0 top-full mt-1 z-20
                 hidden group-hover:block
                 whitespace-nowrap
                 rounded-md bg-gray-900 px-2 py-1
                 text-xs text-white shadow-lg"
              >
                {ticket.customerName}
              </span>
            )}
          </h4>

          <div className="flex items-center gap-2">
            <p className="text-[11px] text-blue-700 font-medium">
              TKT-{ticket?.ticketNumber?.toString().padStart(2, "0")}
            </p>

            <GripVertical
              size={18}
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing w-fit text-gray-400"
            />
          </div>
        </div>

        <div onClick={() => setIsModalOpen(true)} className="cursor-pointer">
          <p className="text-xs text-gray-500 mt-1">
            Serial No:
            <span className="font-medium text-gray-700">
              {" "}
              {ticket.serialNumber}
            </span>
          </p>

          <p className="text-xs text-gray-600 mt-1">
            Mobile:
            <span className="font-medium text-gray-800">
              {" "}
              {ticket.customerMobileNumber}
            </span>
          </p>

          <div className="mt-3 flex justify-between items-center">
            <span className="text-gray-700 text-xs font-semibold rounded-lg">
              {ticket.customerAddress.pincode}
            </span>
            <span className="text-xs">{ticket?.assignee?.username}</span>
          </div>
        </div>
      </div>

      <ServiceTicketCardModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        ticket={ticket}
      />
    </>
  );
};

export default ServiceKanbanCard;
